import React, {useEffect, useMemo, useState} from 'react';
import styles from './PartnerBanner.module.css';
import {useLandingPagesContext} from '../../contexts/LandingPageContext';
import {useCanalPlus} from '../../hooks/UseCanalPlus';
import {navigateToDifferentChapterUrl} from '../../utils';
import {DaznChapterEnum} from '../../utils/dazn/DaznChapterEnum';
import {getCanalPlusParterRedirectUrl} from '../../features/checkIfCanalPlusEnabled';
import {checkIfCanalPlusUserIsTravelling} from './CanalPlusUtils';
import {useAuthenticationService} from '../../hooks/useAuthenticateService';
import Splash from '../Splash/Splash';
import Button, {ButtonTypes} from '../Button/Button';
import {triggerElementClick} from '../../analytics/fe-events';
import {elementClickEventTypes} from '../../analytics/fe-events/elementClick';
import {useAmazonLogin} from '../../hooks/useAmazonLogin';
import {
    checkIsAmazonLP,
    checkIsCanalPlusLP
} from '../../utils/LandingPageUtils';
import {getAmazonLpClientdId} from './AmazonLpUtils';
import {InfoIcon} from '../Icons';
import Markdown from 'markdown-to-jsx';

export const FullScreenBanner = ({data}: {data: any}) => {
    const [amazonAuthorizationCode, setAmazonAuthorizationCode] = useState<
        string | null
    >(null);
    const [width, setWidth] = useState<number>(window.innerWidth);
    const [showSplash, setShowSplash] = useState(true);

    const isCanalPlusLP = checkIsCanalPlusLP();
    const isAmazonLp = checkIsAmazonLP();

    const choosePartnerUserData = () => {
        if (isAmazonLp) {
            return amazonPartnerUserData;
        } else {
            return partnerUserData;
        }
    };

    const {setShowModal, setModalData} = useLandingPagesContext();
    const {
        partnerUserData,
        authorizationCode,
        isNotSubscribedUser,
        isFetchingPartnerUserDetails
    } = useCanalPlus();
    const {amazonPartnerUserData, isFetchingAmazonPartnerDetails, statusCode} =
        useAmazonLogin({amazonAuthorizationCode});
    const {authenticationData, isFetchingAuthenticationService}: any =
        useAuthenticationService(choosePartnerUserData());

    const handleWindowSizeChange = () => setWidth(window.innerWidth);

    const initializeAmazonSdk = () => {
        const amazonClientId = getAmazonLpClientdId();

        if (!document.getElementById('amazon-root')) {
            const amazonRoot = document.createElement('div');
            amazonRoot.id = 'amazon-root';
            document.body.appendChild(amazonRoot);
        }

        if (!document.getElementById('amazon-login-sdk')) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.id = 'amazon-login-sdk';
            script.src = 'https://assets.loginwithamazon.com/sdk/na/login1.js';
            script.onload = () => {
                (window as any).amazon.Login.setClientId(amazonClientId);
            };
            document.getElementById('amazon-root')?.appendChild(script);
        }
    };

    const setupAmazonLogin = () => {
        const intervalId = setInterval(() => {
            const loginButton = document.getElementById('LoginWithAmazon');
            if (loginButton) {
                clearInterval(intervalId);
                loginButton.addEventListener('click', () => {
                    (window as any).amazon.Login.authorize(
                        {
                            scope: 'profile aivsubs:benefits',
                            response_type: 'code',
                            scope_data: {profile: {essential: false}}
                        },
                        (response: any) => {
                            if (!response.error) {
                                setAmazonAuthorizationCode(response.code);
                            }
                        }
                    );
                });
            }
        }, 100);
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () =>
            window.removeEventListener('resize', handleWindowSizeChange);
    }, []);

    useEffect(() => {
        initializeAmazonSdk();
        setupAmazonLogin();
    }, []);

    const ErrorCode = useMemo(() => {
        if (isCanalPlusLP) {
            if (isNotSubscribedUser) return 403;
            if (checkIfCanalPlusUserIsTravelling()) return 401;
        }

        if (isAmazonLp) {
            return statusCode || null;
        }

        return null;
    }, [partnerUserData, isNotSubscribedUser, statusCode]);

    const errorData = useMemo(() => {
        if (!data?.banner?.errorCodes || ErrorCode === null) return null;

        return (
            data.banner.errorCodes.find(
                (errorCode: any) =>
                    errorCode.httpStatusCode === String(ErrorCode)
            ) || null
        );
    }, [data, ErrorCode]);

    useEffect(() => {
        const handleLogout = async () => {
            const isUserLoggedIn = Boolean(window.dazn.auth.token);
            const isCanalPlusLp = window.location.href.includes('canal-plus');

            if (isUserLoggedIn && (isCanalPlusLp || isAmazonLp)) {
                const currentUrl = window.location.href;
                await window.dazn.auth.setToken('');
                window.location.href = currentUrl;
            }
        };

        handleLogout();
    }, []);

    useEffect(() => {
        const MIN_SPLASH_DURATION = 1250;

        if (isAmazonLp && isFetchingAmazonPartnerDetails) {
            setShowSplash(true); // Ensure splash is shown while fetching details
            return;
        }

        const splashTimeout = setTimeout(() => {
            if (
                !isFetchingAuthenticationService &&
                !isFetchingPartnerUserDetails &&
                !isFetchingAmazonPartnerDetails
            ) {
                setShowSplash(false);
            }
        }, MIN_SPLASH_DURATION);

        return () => clearTimeout(splashTimeout);
    }, [
        isFetchingAuthenticationService,
        isFetchingPartnerUserDetails,
        isFetchingAmazonPartnerDetails
    ]);

    useEffect(() => {
        const modalData = errorData
            ? {
                  title: errorData.title,
                  description: errorData.subtitle,
                  buttons: errorData.buttons,
                  showClose: errorData.showClose,
                  backgroundImage: errorData.backgroundImage,
                  httpStatusCode: errorData?.httpStatusCode
              }
            : null;

        if (isCanalPlusLP) {
            switch (partnerUserData?.nextAction) {
                case 'Authenticate':
                    if (authenticationData?.accessToken) {
                        window.dazn.auth.setToken(
                            authenticationData.accessToken
                        );
                        navigateToDifferentChapterUrl({
                            chapterName: DaznChapterEnum.Catalog,
                            chapterPath: '/home',
                            chapterData: {
                                partnerName: 'canal-plus',
                                isAlreadyLinkedUser: true,
                                showUnifiedSignupSuccessBannerForSubscribedUser:
                                    true
                            }
                        });
                    }
                    break;

                case 'SignUp/SignIn-ExplictLink':
                    navigateToDifferentChapterUrl({
                        chapterName: DaznChapterEnum.Auth,
                        chapterPath: '/account/content/DAZN/signup',
                        chapterData: {
                            partnerName: 'canal-plus',
                            canalPlusToken: authorizationCode,
                            providerCustomerId:
                                partnerUserData?.providerCustomerId
                        }
                    });
                    break;

                default:
                    if (errorData) {
                        setShowModal(true);
                        setModalData(modalData);
                    }
                    break;
            }
        } else if (isAmazonLp) {
            const {
                email,
                firstName,
                lastName,
                accessToken,
                nextAction,
                providerCustomerId
            } = amazonPartnerUserData || {};

            switch (nextAction) {
                case 'SignUp-ExplicitLink':
                case 'SignIn-ExplicitLink':
                    navigateToDifferentChapterUrl({
                        chapterName: DaznChapterEnum.Auth,
                        chapterPath: '/account/content/DAZN/signup',
                        chapterData: {
                            LwaData: {
                                email,
                                firstName,
                                lastName,
                                accessToken,
                                providerCustomerId
                            }
                        }
                    });
                    break;

                case 'Authenticate':
                    if (authenticationData?.accessToken) {
                        window.dazn.auth.setToken(
                            authenticationData.accessToken
                        );
                        navigateToDifferentChapterUrl({
                            chapterName: DaznChapterEnum.Catalog,
                            chapterPath: '/home',
                            chapterData: {
                                partnerName: 'amazon',
                                isAlreadyLinkedUser: true,
                                showUnifiedSignupSuccessBannerForSubscribedUser:
                                    true
                            }
                        });
                    }
                    break;
                default:
                    if (errorData) {
                        setShowModal(true);
                        setModalData(modalData);
                    }
                    break;
            }
        }
    }, [
        partnerUserData?.nextAction,
        authorizationCode,
        setShowModal,
        setModalData,
        isNotSubscribedUser,
        errorData,
        authenticationData?.accessToken,
        amazonPartnerUserData?.nextAction,
        amazonAuthorizationCode
    ]);

    const handleCtaClick = () => {
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: 'get_started_cta',
            resourceString: data?.banner?.buttonLabel
        });

        if (isCanalPlusLP) {
            const partnerUrl = getCanalPlusParterRedirectUrl();
            if (partnerUrl) {
                window.location.href = partnerUrl;
            }
        }
    };

    const getDataTestId = () => {
        if (isAmazonLp) return 'LoginWithAmazon';
        if (isCanalPlusLP) return 'LoginWithCanalPlus';
        return undefined;
    };

    const showDisclaimerText = data?.banner?.bannerDisclaimer;

    return showSplash ? (
        <Splash />
    ) : (
        <div className={styles.base}>
            <img
                className={styles.pictureWrapper}
                src={
                    width > 768
                        ? data?.banner?.bannerBackground?.default
                        : data?.banner?.bannerBackground?.mobile
                }
                alt="Banner"
            />

            <div className={styles.baseContainer}>
                <div className={styles.content}>
                    <div className={styles.copy}>
                        <img
                            src={data?.banner?.bannerLogo?.src}
                            className={styles.LogoStyles}
                            alt="Logo"
                        />

                        <div className={styles.title}>
                            {data?.banner?.bannerTitle}
                        </div>

                        <div className={styles.description}>
                            {data?.banner?.bannerDescription}
                        </div>

                        <Button
                            label={data?.banner?.buttonLabel}
                            onClick={handleCtaClick}
                            className={styles.button}
                            buttonTheme={ButtonTypes.PRIMARY}
                            id={getDataTestId()}
                        />

                        {showDisclaimerText && (
                            <div className={styles.disclaimerContainer}>
                                <div className={styles.infoIconContainer}>
                                    <InfoIcon />
                                </div>
                                <Markdown
                                    as="p"
                                    className={styles.disclaimerText}
                                >
                                    {data?.banner?.bannerDisclaimer}
                                </Markdown>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
